/**
 * Phase 2 Communications — recording + transcript inline panel for
 * `call_started` / `call_ended` timeline bubbles.
 *
 * Contract
 * --------
 *   - Renders NOTHING unless `logicalCallId` is present. Historical
 *     bubbles (pre-Phase-1) never had one; those must stay untouched.
 *   - "▶ Play recording" is lazy: no signed URL is minted (nor bytes
 *     downloaded) until the operator clicks Play.
 *   - "View transcript" reveals the segment list, labeled Agent /
 *     Customer per speaker channel. When the transcript is a flat
 *     fallback (`unknown` role) we hide the role label and show only
 *     the text so we never fabricate speaker attribution.
 *   - Loading, unavailable, failed states all render as compact, kind
 *     one-liners — never a stack trace or a technical hint.
 *   - No provider terminology anywhere ("Telnyx", "WebRTC" are for
 *     admin diagnostics only).
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Play, Loader2 } from 'lucide-react';
import useLogicalCall from '../../hooks/useLogicalCall';
import { useCompany } from '../../context/CompanyContext';

const _roleLabel = (role) => {
  if (role === 'agent')    return 'Agent';
  if (role === 'customer') return 'Customer';
  return '';
};

function TranscriptView({ transcript }) {
  const state = transcript?.state || 'pending';
  const segs  = Array.isArray(transcript?.segments) ? transcript.segments : [];

  if (state === 'pending' || state === 'processing') {
    return (
      <div className="mt-1.5 text-[11px] italic text-slate-500" data-testid="call-transcript-processing">
        Transcribing call…
      </div>
    );
  }
  if (state === 'unavailable' || state === 'failed') {
    return (
      <div className="mt-1.5 text-[11px] italic text-slate-400" data-testid="call-transcript-unavailable">
        Transcript unavailable.
      </div>
    );
  }
  if (!segs.length) {
    return (
      <div className="mt-1.5 text-[11px] italic text-slate-400" data-testid="call-transcript-empty">
        No speech detected.
      </div>
    );
  }
  return (
    <div className="mt-1.5 space-y-1.5" data-testid="call-transcript-segments">
      {segs.map((s, i) => {
        const label = _roleLabel(s.role);
        const roleCls = s.role === 'agent'
          ? 'text-emerald-800'
          : s.role === 'customer' ? 'text-blue-800' : 'text-slate-600';
        return (
          <div key={i} className="text-[12px] leading-relaxed" data-testid={`call-transcript-seg-${s.role || 'unknown'}`}>
            {label && (
              <span className={`font-semibold mr-1 ${roleCls}`}>{label}:</span>
            )}
            <span className="text-slate-800 whitespace-pre-wrap">{s.text}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function LogicalCallPanel({ logicalCallId }) {
  const { companyId } = useCompany() || {};
  const enabled = Boolean(logicalCallId && companyId);
  const {
    call, transcript, loading, error,
    audioUrl, audioLoading, audioError, loadAudio,
  } = useLogicalCall({ logicalCallId, companyId, enabled });

  const [showTranscript, setShowTranscript] = useState(false);

  if (!enabled) return null;

  if (loading && !call) {
    return (
      <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400" data-testid="call-panel-loading">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading recording…
      </div>
    );
  }
  if (error === 'not_found') return null;   // pre-deploy / not yet materialized
  if (error === 'forbidden') return null;   // paranoid — server already 403s
  if (error) return null;                   // graceful — never trip the bubble

  const recAvailable = call?.recordingRefs?.state === 'available';
  const showTranscriptSection = Boolean(transcript);

  return (
    <div className="mt-1.5 border-t border-slate-200 pt-1.5" data-testid="call-panel">
      <div className="flex flex-wrap items-center gap-2">
        {recAvailable ? (
          audioUrl ? (
            <audio
              controls
              preload="metadata"
              src={audioUrl}
              className="max-w-full w-64"
              data-testid="call-panel-audio"
            />
          ) : (
            <button
              type="button"
              onClick={loadAudio}
              disabled={audioLoading}
              data-testid="call-panel-play-recording"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 hover:text-emerald-900 rounded-md px-1.5 py-0.5 hover:bg-emerald-100/60 transition-colors disabled:opacity-60"
            >
              {audioLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Play className="w-3 h-3" />
              )}
              Play recording
            </button>
          )
        ) : (
          <span className="text-[11px] italic text-slate-400" data-testid="call-panel-no-recording">
            Recording unavailable
          </span>
        )}

        {audioError && (
          <span className="text-[11px] italic text-slate-400" data-testid="call-panel-audio-error">
            Couldn’t load audio.
          </span>
        )}

        {showTranscriptSection && (
          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            aria-expanded={showTranscript}
            data-testid="call-panel-transcript-toggle"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 hover:text-slate-900 rounded-md px-1.5 py-0.5 hover:bg-slate-100 transition-colors"
          >
            {showTranscript
              ? <ChevronUp   className="w-3 h-3" />
              : <ChevronDown className="w-3 h-3" />}
            {showTranscript ? 'Hide transcript' : 'View transcript'}
          </button>
        )}
      </div>

      {showTranscript && showTranscriptSection && (
        <TranscriptView transcript={transcript} />
      )}
    </div>
  );
}
